import { FiLock } from '@/components/react-icons/main';
import Tag from '@/components/Tag';
import IconV2 from '@/components/v2/IconV2';
import credentialActions from '@/redux/actions/credentialActions';
import waitForSyncReady from '@/redux/actions/waitForSyncReady';
import * as Popover from '@radix-ui/react-popover';
import { appSchema } from '@shared/schema/appSchema.js';
import { useCallback, useMemo, useRef, useState } from 'react';

const authTypeDescriptions = {
  oAuth2: "Sign in through the app's website to securely approve access.",
  oAuth1: "Sign in through the app's website to securely approve access.",
  basic: 'Sign in using your username and password.',
  apiKey: 'Enter a key or access token from the app.',
  ftp: 'Enter your FTP server login details.',
  custom: 'Enter the authentication details required.',
  none: 'No authentication is required.'
};

const AuthTypeListItem = ({ authType, onSelect, currentAuthType, currentAuthId }) => {
  const authTypeInfo = appSchema?.authentication?.types?.find((t) => t.type === authType.type);
  const label = authType.name || authTypeInfo?.label || authType.type;
  const description = authTypeDescriptions[authType.type] || authType.description || '';
  const isActive = !currentAuthId ? currentAuthType === authType?.type : currentAuthId === authType.id;

  return (
    <div key={authType.id} data-active={isActive} className="item" onClick={() => onSelect(authType)} tabIndex={0}>
      <div className="item-content">
        <div className="item-header">
          <span className="item-name">{label}</span>
          {isActive && <Tag className="blue">Active</Tag>}
        </div>
        {description && <div className="item-description">{description}</div>}
      </div>
    </div>
  );
};

const AuthTypePopover = ({ app, credential, companyId, onTypeChangeStart, onTypeChangeEnd }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverWidth, setPopoverWidth] = useState(null);
  const triggerRef = useRef(null);

  const currentAuthId = credential?.meta?.authId || credential?.secrets?.authId;
  const currentAuthType = credential?.meta?.type || credential?.secrets?.type;
  const availableAuthTypes = useMemo(() => {
    return (app?.authentication || []).filter((auth) => !auth.hidden);
  }, [app?.authentication]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    if (triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth);
    }
    setIsOpen(true);
  }, []);

  const handleAuthTypeSelect = useCallback(
    async (authType) => {
      if (authType.id === currentAuthId) {
        setIsOpen(false);
        return;
      }

      setIsOpen(false);
      onTypeChangeStart?.();

      const label = credential?.secrets?.label || credential?.meta?.label || '';
      const labelSource = credential?.meta?.labelSource;
      await credentialActions.set({
        appId: app?.id,
        companyId,
        credentialId: credential?.credentialId,
        secrets: { authId: authType.id, type: authType.type, label },
        meta: { authId: authType.id, type: authType.type, label, labelSource }
      });

      await waitForSyncReady();
      onTypeChangeEnd?.();
    },
    [currentAuthId, credential, app?.id, companyId, onTypeChangeStart, onTypeChangeEnd]
  );

  if (availableAuthTypes.length <= 1) {
    return null;
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Popover.Trigger asChild onClick={handleOpen}>
        <div ref={triggerRef} className="comp-panel-section boxed collapsed clickable">
          <div className="comp-panel-section-title">
            <div className="boxed-title">
              <div className="icon">
                <IconV2 icon={FiLock} size={16} />
              </div>
              <div className="text">
                <div className="title">Connection Type</div>
                <div className="description">Change what authentication method is used.</div>
              </div>
            </div>
          </div>
        </div>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className="comp-auth-type-popover" align="start" sideOffset={8} style={popoverWidth ? { width: popoverWidth } : undefined}>
          <div className="body">
            <div className="items-container">
              {availableAuthTypes.length === 0 ? (
                <div className="empty-state">
                  <span>No authentication types available</span>
                </div>
              ) : (
                availableAuthTypes.map((authType) => <AuthTypeListItem key={authType.id} authType={authType} onSelect={handleAuthTypeSelect} currentAuthId={currentAuthId} currentAuthType={currentAuthType} />)
              )}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default AuthTypePopover;
